import { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Image,
  Text,
  Button,
  Grid,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Tooltip,
  useColorModeValue,
  FormLabel,
  HStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Wrap,
  WrapItem,
  Icon,
  FormControl,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Input,
  Stack,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useLocation, Form } from "react-router-dom";
import { useAuth } from "./AuthContext";
import {
  HiEye,
  HiOutlinePlus,
  HiOutlineCode,
  HiOutlineStatusOnline,
  HiOutlineMenu,
} from "react-icons/hi";

import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";

import { useClocks } from "./hooks/useClocks";
import ClockCard from "./components/ClockCard";
import CodexLookupText from "./CodexLookupText";
import CodesetSelect from "./CodesetSelect";

import MyWorkflowList from "./MyWorkflowList.jsx";

const Poc = () => {
  const { user } = useAuth();
  const isUserActive = user?.platformUser?.isActive === "true";
  const isUserSuperAdmin = user?.platformUser?.isSuperAdmin === "true";
  const isUserAdmin = user?.platformUser?.isAdmin === "true";
  const isUserDeveloper = user?.platformUser?.isDeveloper === "true";
  const isProductDesigner = user?.platformUser?.roles?.some(role =>
    (typeof role === 'string' ? role : role.role) === 'product_designer'
  ) || false;
  const navigate = useNavigate();

  const handleOpenParty = (party) => {
    navigate(`/party/${party._id}`);
  };

  const [productsContracts, setProductsContracts] = useState([]);
  const [productsContractsLoading, setProductsContractsLoading] =
    useState(true);

  const [parties, setParties] = useState([]);
  const [partyLoading, setPartyLoading] = useState(true);

  const [registers, setRegisters] = useState([]);
  const [registerLoading, setRegisterLoading] = useState(true);

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    placingStageCode: "",
    placingTransactionFunctionCode: "",
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const cardBg = useColorModeValue("white", "gray.700");

  const { clocks, loading: clocksLoading } = useClocks();

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: "",
      placingStageCode: "",
      placingTransactionFunctionCode: "",
    });
  };

  const handleBackToProducts = () => {
    setSelectedProduct(null);
    setFormData({
      name: "",
      placingStageCode: "",
      placingTransactionFunctionCode: "",
    });
  };

  const handleModalClose = () => {
    setSelectedProduct(null);
    setFormData({
      name: "",
      placingStageCode: "",
      placingTransactionFunctionCode: "",
    });
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        name: formData.name,
        type: "",
        placingStageCode: formData.placingStageCode,
        productCode: selectedProduct.code,
        placingTransactionFunctionCode: formData.placingTransactionFunctionCode,
      };

      const response = await fetch(
        `https://poc.mrc.ai/api/products/${selectedProduct.code}/contracts`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Failed to create contract");

      toast({
        title: "Contract created successfully",
        status: "success",
      });

      handleModalClose();

      // Refresh the contracts list
      const contractsResponse = await fetch(
        "https://poc.mrc.ai/api/products/contracts"
      );
      const contractsData = await contractsResponse.json();
      setProductsContracts(contractsData);
    } catch (err) {
      console.error("Failed to create contract:", err);
      toast({
        title: "Failed to create contract",
        description: err.message,
        status: "error",
      });
    }
  };

  useEffect(() => {
    const fetchProductsContracts = async () => {
      try {
        const response = await fetch(
          "https://poc.mrc.ai/api/products/contracts"
        );
        const data = await response.json();
        setProductsContracts(data);
      } catch (err) {
        console.error("Failed to fetch products contracts:", err);
      } finally {
        setProductsContractsLoading(false);
      }
    };

    const fetchParties = async () => {
      try {
        const res = await fetch("https://directory.poc.mrc.ai/api/parties");
        const data = await res.json();
        setParties(data);
      } catch (err) {
        console.error("Failed to fetch parties:", err);
      } finally {
        setPartyLoading(false);
      }
    };

    const fetchRegisters = async () => {
      try {
        const res = await fetch("https://registers.poc.mrc.ai/api/registers");
        const data = await res.json();
        setRegisters(data);
      } catch (err) {
        console.error("Failed to fetch registers:", err);
      } finally {
        setRegisterLoading(false);
      }
    };

    const fetchProducts = async () => {
      try {
        const res = await fetch("https://poc.mrc.ai/api/products");
        const data = await res.json();
        setProducts(data);
      } catch (err) {
        console.error("Failed to fetch products:", err);
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProductsContracts();
    fetchParties();
    fetchRegisters();
    fetchProducts();
  }, []);


  return (
    <Box height="100vh" display="flex" flexDirection="column" overflow="hidden">
      <Box flexShrink={0}>
        <Navbar />
      </Box>

      {/* Watermark background */}
      <Box
        position="relative"
        width="100%"
        flex="1"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        {/* Overlay content */}
        <Box position="relative" zIndex={1} p={1} display="flex" flexDirection="column" height="100%">
          <Tabs
            orientation="horizontal"
            variant="line"
            colorScheme="linkedin"
            isLazy={true}
            lazyBehavior="unmount"
            display="flex"
            flexDirection="column"
            height="100%">
            <TabList>
              <Tab>Dashboard</Tab>
              {(isProductDesigner || isUserAdmin || isUserSuperAdmin) && <Tab>Parties</Tab>}
              <Tab>Registers</Tab>
            </TabList>
            <TabPanels flex="1" overflow="auto">
              {/* Dashboard - Always visible */}
              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                {/* Platform Clocks */}
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  mb={2}
                  flexShrink={0}>
                  {clocksLoading ? (
                    <Spinner />
                  ) : (
                    <Wrap spacing={4}>
                      {clocks.map((clock) => (
                        <ClockCard key={clock._id} clock={clock} />
                      ))}
                    </Wrap>
                  )}
                </Box>
                {/* Dashboard Panel */}
                <Box flex="1" minHeight={0}>
                  <Grid
                    templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
                    templateRows={{ base: "auto", lg: "1fr 1fr" }}
                    gap={2}
                    width="100%"
                    height="100%">

                    {/* My Workflow box */}
                    <Box
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                      borderColor="gray.500"
                      backgroundColor="whiteAlpha.800"
                      display="flex"
                      flexDirection="column"
                      overflow="hidden">
                      <Heading size="sm" mb={2}>
                        My Workflow
                      </Heading>
                      <Box flex="1" overflow="auto">
                        <MyWorkflowList />
                      </Box>
                    </Box>

                    {/* My Contracts box */}
                    <Box
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                      borderColor="gray.500"
                      backgroundColor="whiteAlpha.800"
                      gridRow={{ base: "auto", lg: "1 / span 2" }}
                      display="flex"
                      flexDirection="column"
                      overflow="hidden">
                      <HStack justify="space-between" mb={2}>
                        <Heading size="sm">
                          My Contracts
                        </Heading>
                        <Tooltip label="Add Contract">
                          <IconButton
                            icon={<HiOutlinePlus />}
                            size="sm"
                            onClick={onOpen}
                            aria-label="Add Contract"
                          />
                        </Tooltip>
                      </HStack>
                      <Box flex="1" overflow="auto">
                        <Table
                          variant="unstyled"
                          size="sm"
                          fontSize="xs"
                          style={{ tableLayout: "auto", width: "100%" }}
                          sx={{
                            "tbody tr:nth-of-type(odd)": {
                              backgroundColor: "gray.200",
                            },
                          }}>
                          <Thead position="sticky" top={0} bg="white" zIndex={1}>
                            <Tr>
                              <Th fontSize="xs">Product</Th>
                              <Th fontSize="xs">Product Name</Th>
                              <Th fontSize="xs">Contract Name</Th>
                              <Th fontSize="xs">Placing Stage</Th>
                              <Th fontSize="xs">Transaction Function</Th>
                              <Th fontSize="xs"></Th>
                            </Tr>
                          </Thead>

                          <Tbody>
                            {productsContracts.map((productcontract) => (
                              <Tr key={productcontract.id}>
                                <Td fontSize="xs">{productcontract.code}</Td>
                                <Td fontSize="xs">{productcontract.name}</Td>
                                <Td fontSize="xs">
                                  {productcontract.contractName}
                                </Td>
                                <Td fontSize="xs">
                                  {productcontract.contractPlacingStageCode}
                                </Td>
                                <Td fontSize="xs">
                                  {
                                    productcontract.contractPlacingTransactionFunctionCode
                                  }
                                </Td>
                                <Td fontSize="xs">
                                  <Tooltip
                                    label="Edit Contract"
                                    aria-label="Edit Contract Tooltip">
                                    <IconButton
                                      icon={<HiOutlineMenu />}
                                      size="md"
                                      onClick={() =>
                                        navigate(
                                          `/product/${productcontract.code}/contract/${productcontract.contractId}/contract`
                                        )
                                      }
                                      _hover={{ transform: "scale(1.1)" }}
                                      transition="transform 0.2s ease"
                                    />
                                  </Tooltip>
                                  <Tooltip
                                    label="Edit Placement"
                                    aria-label="Edit Placement Tooltip">
                                    <IconButton
                                      icon={<HiOutlineCode />}
                                      size="md"
                                      onClick={() =>
                                        navigate(
                                          `/product/${productcontract.code}/contract/${productcontract.contractId}/placement`
                                        )
                                      }
                                      _hover={{ transform: "scale(1.1)" }}
                                      transition="transform 0.2s ease"
                                    />
                                  </Tooltip>

                                  <Tooltip
                                    label="Create Standard Signal"
                                    aria-label="Create Standard Signal Tooltip">
                                    <IconButton
                                      icon={<HiOutlineStatusOnline />}
                                      size="md"
                                      onClick={() =>
                                        navigate(
                                          `/product/${productcontract.code}/contract/${productcontract.contractId}/signal`
                                        )
                                      }
                                      _hover={{ transform: "scale(1.1)" }}
                                      transition="transform 0.2s ease"
                                    />
                                  </Tooltip>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>

                    {/* My Registers box */}
                    <Box
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                      borderColor="gray.500"
                      backgroundColor="whiteAlpha.800"
                      display="flex"
                      flexDirection="column"
                      overflow="hidden">
                      <Heading size="sm" mb={2}>
                        My Registers
                      </Heading>
                      <Box flex="1" overflow="auto">
                        <Box>
                          <Box
                            borderWidth="1px"
                            borderRadius="sm"
                            mb={1}
                            borderColor={"gray.500"}
                            backgroundColor="whiteAlpha.800">
                            <HStack>
                              <Tooltip label="Add register">
                                <IconButton
                                  icon={<HiOutlinePlus />}
                                  aria-label="Add register"
                                  size="sm"
                                  onClick={() => navigate("/registers/new")}
                                />
                              </Tooltip>
                            </HStack>
                          </Box>

                          {registerLoading ? (
                            <Spinner />
                          ) : (
                            <Wrap spacing={4}>
                              {registers.map((register) => (
                                <WrapItem key={register._id}>
                                  <Box
                                    bg={cardBg}
                                    p={4}
                                    rounded="lg"
                                    shadow="md"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    minW="200px"
                                    maxW="fit-content"
                                    transition="all 0.2s ease"
                                    _hover={{
                                      shadow: "lg",
                                      transform: "translateY(-4px)",
                                    }}>
                                    <HStack>
                                      <Tooltip label="Edit Register...">
                                        <IconButton
                                          icon={<HiEye />}
                                          aria-label="Edit register"
                                          size="sm"
                                          onClick={() =>
                                            navigate(
                                              `/registers/${register._id}`
                                            )
                                          }
                                        />
                                      </Tooltip>
                                      <Heading size="sm" mb={0} noOfLines={1}>
                                        {register.name}
                                      </Heading>
                                    </HStack>
                                  </Box>
                                </WrapItem>
                              ))}
                            </Wrap>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Box>
              </TabPanel>


              {/* Parties - product_designer, isAdmin, or isSuperAdmin */}
              {(isProductDesigner || isUserAdmin || isUserSuperAdmin) && (
                <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                  {/* Directory */}
                  <Box
                    borderWidth="1px"
                    borderRadius="md"
                    p={1}
                    borderColor={"gray.500"}
                    backgroundColor="whiteAlpha.800"
                    flex="1"
                    overflow="auto">
                    <Box
                      borderWidth="1px"
                      borderRadius="md"
                      p={1}
                      mb={2}
                      borderColor={"gray.500"}
                      backgroundColor="whiteAlpha.800">
                      <HStack>
                        <Tooltip label="Add party">
                          <IconButton
                            icon={<HiOutlinePlus />}
                            aria-label="Add party"
                            size="md"
                            onClick={() => navigate("/party/new")}
                          />
                        </Tooltip>
                      </HStack>
                    </Box>

                    {partyLoading ? (
                      <Spinner />
                    ) : (
                      <Wrap spacing={4}>
                        {parties.map((party) => (
                        <WrapItem key={party._id}>
                          <Box
                            bg={cardBg}
                            p={4}
                            rounded="lg"
                            shadow="md"
                            border="1px solid"
                            borderColor="gray.300"
                            minW="200px"
                            maxW="fit-content"
                            transition="all 0.2s ease"
                            _hover={{
                              shadow: "lg",
                              transform: "translateY(-4px)",
                            }}>
                            <HStack>
                              <Tooltip label="Edit party details">
                                <IconButton
                                  icon={<HiEye />}
                                  aria-label="Edit party"
                                  size="sm"
                                  onClick={() => handleOpenParty(party)}
                                />
                              </Tooltip>
                              <Heading size="sm" mb={0} noOfLines={1}>
                                {party.name}
                              </Heading>
                            </HStack>
                            <Text fontSize="xs" color="gray.500" mt={2}>
                              {party.roleCode}
                            </Text>
                          </Box>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>
              </TabPanel>
              )}

              {/* Registers - Always visible */}
              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                {/* Registers */}
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  <Box
                    borderWidth="1px"
                    borderRadius="md"
                    p={1}
                    mb={2}
                    borderColor={"gray.500"}
                    backgroundColor="whiteAlpha.800">
                    <HStack>
                      <Tooltip label="Add register">
                        <IconButton
                          icon={<HiOutlinePlus />}
                          aria-label="Add register"
                          size="md"
                          onClick={() => navigate("/registers/new")}
                        />
                      </Tooltip>
                    </HStack>
                  </Box>

                  {registerLoading ? (
                    <Spinner />
                  ) : (
                    <Wrap spacing={4}>
                      {registers.map((register) => (
                        <WrapItem key={register._id}>
                          <Box
                            bg={cardBg}
                            p={4}
                            rounded="lg"
                            shadow="md"
                            border="1px solid"
                            borderColor="gray.300"
                            minW="200px"
                            maxW="fit-content"
                            transition="all 0.2s ease"
                            _hover={{
                              shadow: "lg",
                              transform: "translateY(-4px)",
                            }}>
                            <HStack>
                              <Tooltip label="Edit Register...">
                                <IconButton
                                  icon={<HiEye />}
                                  aria-label="Edit register"
                                  size="sm"
                                  onClick={() =>
                                    navigate(`/registers/${register._id}`)
                                  }
                                />
                              </Tooltip>
                              <Heading size="sm" mb={0} noOfLines={1}>
                                {register.name}
                              </Heading>
                            </HStack>
                          </Box>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>
              </TabPanel>




            </TabPanels>
          </Tabs>
        </Box>
      </Box>

      {/* Product Selection and Contract Creation Modal */}
      <Modal isOpen={isOpen} onClose={handleModalClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent
          maxW="600px"
          bg="white"
          borderRadius="xl"
          boxShadow="lg"
          border="1px solid"
          px={6}>
          <ModalHeader fontSize="xl" fontWeight="bold">
            {selectedProduct ? "Add Contract Header" : "Select Product"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {!selectedProduct ? (
              // Step 1: Product Selection
              <>
                <Text fontSize="sm" mb={4} color="gray.600">
                  Choose a product to create a new contract:
                </Text>
                {productsLoading ? (
                  <Spinner />
                ) : (
                  <SimpleGrid columns={1} spacing={3}>
                    {products.map((product) => (
                      <Box
                        key={product._id}
                        bg={cardBg}
                        p={4}
                        rounded="lg"
                        shadow="md"
                        border="1px solid"
                        borderColor="gray.300"
                        cursor="pointer"
                        transition="all 0.2s ease"
                        _hover={{
                          shadow: "lg",
                          transform: "translateY(-2px)",
                          borderColor: "blue.400",
                        }}
                        onClick={() => handleProductSelect(product)}>
                        <Heading size="sm" mb={1}>
                          {product.name}
                        </Heading>
                        <Text fontSize="xs" color="gray.500" mb={1}>
                          Code: {product.code}
                        </Text>
                        {product.description && (
                          <Text fontSize="xs" color="gray.600">
                            {product.description}
                          </Text>
                        )}
                      </Box>
                    ))}
                  </SimpleGrid>
                )}
              </>
            ) : (
              // Step 2: Contract Form
              <Box
                as="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}>
                <Text fontSize="sm" mb={4} color="gray.600">
                  Product: <strong>{selectedProduct.name}</strong> ({selectedProduct.code})
                </Text>
                <Stack spacing={4}>
                  <Input
                    placeholder="Contract Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                  <CodesetSelect
                    label="Placing Stage"
                    codesetCode="A83"
                    value={
                      formData.placingStageCode
                        ? {
                            code: formData.placingStageCode,
                            description: "",
                          }
                        : null
                    }
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        placingStageCode: value.code,
                      })
                    }
                  />
                  <CodesetSelect
                    label="Placing Transaction"
                    codesetCode="1225"
                    value={
                      formData.placingTransactionFunctionCode
                        ? {
                            code: formData.placingTransactionFunctionCode,
                            description: "",
                          }
                        : null
                    }
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        placingTransactionFunctionCode: value.code,
                      })
                    }
                  />
                </Stack>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            {selectedProduct && (
              <Button onClick={handleBackToProducts} size="sm" mr={2}>
                Back
              </Button>
            )}
            <Button onClick={handleModalClose} size="sm" mr={2}>
              Cancel
            </Button>
            {selectedProduct && (
              <Button onClick={handleSubmit} colorScheme="blue" size="sm">
                Create
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Poc;
